// Let controls and browser zoom keep their native gestures. Scroll boundaries
// are checked separately, once the direction of a gesture is known.
export function canStartSwipe(target, container, scale = 1) {
  if (!container || !target || scale > 1.01) return false;
  const element = target.nodeType === 1 ? target : target.parentElement;
  if (!element || !container.contains(element)) return false;
  // The video stage is a large tap-to-play surface, not a scrubber. A
  // vertical gesture there should still navigate; its control bar must not.
  const control = element.closest('a, button, input, textarea, select, [role="slider"], [role="button"], [contenteditable="true"], [data-no-swipe]');
  if (control && !control.matches('.recorded-media__video-toggle')) return false;

  return true;
}

// Snapshot at the start, not after native scrolling has already reached an
// edge. The rest of that gesture must finish scrolling the same card.
export function nativeScrollDirections(target, container) {
  const directions = { up: false, down: false };
  for (let node = target?.nodeType === 1 ? target : target?.parentElement;
    node && node !== container; node = node.parentElement) {
    const range = node.scrollHeight - node.clientHeight;
    if (range <= 1 || !/(auto|scroll)/.test(window.getComputedStyle(node).overflowY)) continue;
    // Safari can report positions outside the scroll range during rubber-band.
    const top = Math.max(0, Math.min(range, node.scrollTop));
    directions.up ||= top < range - 1;
    directions.down ||= top > 1;
  }
  return directions;
}

export const newWheelGesture = () => ({ at: null, startedAt: null, sign: 0, distance: 0,
  consumed: false, native: false, lastDelta: 0, lastMode: 0, steadyTicks: 0 });

// One trackpad gesture (including its momentum) advances one card. A pause or
// an intentional reversal starts another. Repeated coarse mouse notches also
// rearm at animation pace; they need no artificial pause to keep moving.
// Retain this state across card swaps.
export function wheelNavigation(gesture, event, pageHeight, scrollDirections, now = Date.now()) {
  const { deltaX, deltaY } = event;
  if (event.ctrlKey || event.metaKey || event.shiftKey || !Number.isFinite(deltaY)
      || Math.abs(deltaY) <= Math.abs(deltaX) * 1.25 || deltaY === 0) return { claimed: false };
  const sign = Math.sign(deltaY);
  const direction = sign > 0 ? 'up' : 'down';
  const gap = gesture.at === null ? 0 : now - gesture.at;
  const coarse = event.deltaMode === 1 || event.deltaMode === 2 || Math.abs(deltaY) >= 80
    || (Math.abs(deltaY) >= 40 && gap >= 60 && gap <= 180);
  const steadyTicks = coarse && deltaY === gesture.lastDelta && event.deltaMode === gesture.lastMode
    ? gesture.steadyTicks + 1 : 0;
  const continuingMouse = steadyTicks >= 2 && now - gesture.startedAt >= 280;
  if (gesture.at === null || gap > 180 || gesture.sign !== sign || continuingMouse) {
    Object.assign(gesture, newWheelGesture(), { sign, startedAt: now, native: scrollDirections()[direction] });
  } else {
    gesture.steadyTicks = steadyTicks;
  }
  gesture.at = now;
  gesture.lastDelta = deltaY;
  gesture.lastMode = event.deltaMode;
  if (gesture.native) return { claimed: false };
  const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? Math.max(1, pageHeight) : 1;
  gesture.distance += deltaY * unit;
  if (!gesture.consumed && Math.abs(gesture.distance) > 45) {
    gesture.consumed = true;
    return { claimed: true, direction };
  }
  return { claimed: true };
}

export function getSwipeDirection(start, end, threshold = null) {
  const elapsed = end.time - start.time;
  const distance = Math.abs(start.y - end.y);
  // Short, intentional flicks work without turning small taps into swipes.
  const minimum = threshold ?? (elapsed > 0 && elapsed < 250 && distance / elapsed >= 0.35 ? 24 : 45);
  const vertical = start.y - end.y;
  const horizontal = start.x - end.x;
  if (Math.abs(vertical) <= minimum || Math.abs(vertical) <= Math.abs(horizontal) * 1.25) return null;
  return vertical > 0 ? 'up' : 'down';
}
